/**
 * AdminModals composes all modal/dialog components used across the Admin dashboard.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Plus, X, Utensils, Image as ImageIcon, MoveUp, MoveDown, Trash2, Filter, Search, Copy, Check, Video, Leaf, Flame, Drumstick, Fish } from 'lucide-react';
import { mediaSrc, adminAPI } from './AdminUtils';
import { useAdmin } from './AdminHooks';

const toInstagramEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('instagram.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) {
        const kind = parts[0];
        const code = parts[1];
        return `https://www.instagram.com/${kind}/${code}/embed`;
      }
    }
  } catch {}
  return null;
};

const MenuSelector = () => {
  const { reservationMenuOptions, reservationMenuSelections, setMenuSelectionQuantity } = useAdmin();
  if (!reservationMenuOptions || reservationMenuOptions.length === 0) {
    return <div className="p-4 text-gray-400 bg-gray-900 rounded-lg border border-white/10">Aucun menu défini pour cet événement</div>;
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        {reservationMenuOptions.map((dish, idx) => {
          const sel = reservationMenuSelections.find(s => s.name === dish.name);
          const qty = sel ? sel.quantity : 0;
          return (
            <div key={idx} className="flex justify-between items-center p-3 bg-gray-900 rounded-lg border border-white/10">
              <div className="flex-1">
                <div className="font-medium text-white">{dish.name}</div>
                <div className="text-xs text-gray-400">{dish.type}{dish.vegetarian ? ' • Végétarien' : ''}{dish.price != null ? ` • ${dish.price}€` : ''}</div>
              </div>
              <div className="flex gap-2 items-center">
                <Label className="text-gray-400"></Label>
                <Input
                  type="number"
                  min="0"
                  value={qty}
                  onChange={(e) => setMenuSelectionQuantity(dish.name, parseInt(e.target.value || '0'))}
                  className="w-20 text-white bg-gray-800 border-gray-700"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminModals = () => {
  const {
    // modal flags
    isEventModalOpen, setIsEventModalOpen,
    isMenuModalOpen, setIsMenuModalOpen,
    isMediaModalOpen, setIsMediaModalOpen, isLineupModalOpen, setIsLineupModalOpen,
    isSelectionModalOpen, setIsSelectionModalOpen,
    isAddModalOpen, setIsAddModalOpen,
    isEditModalOpen, setIsEditModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isDeleteClientModalOpen, setIsDeleteClientModalOpen,
    // state and data
    selectedReservation, selectedEvent, selectedClient,
    formData, setFormData, eventFormData, setEventFormData, menuFormData, lineupFormData, setLineupFormData,
    clients, events,
    mediaItems, uploadingMedia, mediaActiveUpdating, mediaActiveMessage,
    featuredItems, featuredLoading,
    selectionTab, setSelectionTab, selectionFilter, setSelectionFilter, selectionQuery, setSelectionQuery,
    selectionList, selectionLoading, selectionSelectedIds, toggleSelection, selectionMode, selectionEventId, setSelectionEventId,
    uploadProgress,
    // actions
    handleSaveEvent, handleDeleteReservation, handleEditReservation, handleAddReservation,
    handleSaveMenu, handleUploadMedia, toggleEventMediaActive, openSelectionModal, addSelectedToFeatured, addSelectedToEvent, handleSaveLineup,
    fetchFeaturedMedia, openFeaturedModal, moveFeatured, deleteFeaturedItem, updateFeaturedItem,
    moveMedia, deleteMedia, updateMedia,
    copyEmailsToClipboard,
    loadSelectionList,
    addDish, addDishOfType, removeDish, updateDish, addIngredient, updateIngredient, removeIngredient, addArtist, updateArtist, removeArtist,
    handleDeleteClient,
    resetForm,
    resetEventForm,
  } = useAdmin();

  return (
    <>
      {/* Event Modal */}
      <Dialog open={isEventModalOpen} onOpenChange={(open) => { setIsEventModalOpen(open); if (!open) resetEventForm(); }}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white w-[95vw] sm:w-auto sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{selectedEvent ? 'Modifier' : 'Nouvel'} événement</DialogTitle>
            <DialogDescription>Créer ou modifier les détails d’un événement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-400">Volume</Label><Input type="number" min="1" value={eventFormData.volume} onChange={(e) => setEventFormData(prev => ({ ...prev, volume: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" placeholder="2" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Statut</Label>
                <Select value={eventFormData.status} onValueChange={(value) => setEventFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="text-white bg-gray-900 border-gray-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700"><SelectItem value="upcoming" className="text-white">À venir</SelectItem><SelectItem value="available" className="text-white">Ouvert</SelectItem><SelectItem value="sold_out" className="text-white">Complet</SelectItem><SelectItem value="past" className="text-white">Terminé</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label className="text-gray-400">Thème</Label><Input value={eventFormData.theme} onChange={(e) => setEventFormData(prev => ({ ...prev, theme: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" placeholder="MOUNTAIN TECHNO" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-400">Date</Label><Input value={eventFormData.date} onChange={(e) => setEventFormData(prev => ({ ...prev, date: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" placeholder="4 DÉCEMBRE 2024" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Horaire</Label><Input value={eventFormData.time} onChange={(e) => setEventFormData(prev => ({ ...prev, time: e.target.value }))} className="text-white bg-gray-900 border-gray-700" placeholder="19H - 00H" /></div>
            </div>
            <div className="space-y-2"><Label className="text-gray-400">Prix (€)</Label><Input type="number" min="0" value={eventFormData.price} onChange={(e) => setEventFormData(prev => ({ ...prev, price: e.target.value }))} className="text-white bg-gray-900 border-gray-700" placeholder="30" /></div>
            <div className="space-y-2"><Label className="text-gray-400">Description</Label><Textarea value={eventFormData.description} onChange={(e) => setEventFormData(prev => ({ ...prev, description: e.target.value }))} className="text-white bg-gray-900 border-gray-700" placeholder="Description..." /></div>
            <div className="flex justify-between items-center p-4 bg-gray-900 rounded-lg"><div><Label className="text-white">Réservations ouvertes</Label><p className="text-sm text-gray-500">Permettre les réservations</p></div><Switch checked={eventFormData.reservationsOpen} onCheckedChange={(checked) => setEventFormData(prev => ({ ...prev, reservationsOpen: checked }))} /></div>
            <div className="flex justify-between items-center p-4 bg-gray-900 rounded-lg">
              <div>
                <Label className="text-white">Créneaux pour cet événement</Label>
                <p className="text-sm text-gray-500">Permettre le choix d&apos;une heure le jour J</p>
              </div>
              <Switch checked={!!eventFormData.timeSlotsEnabled} onCheckedChange={(checked) => setEventFormData(prev => ({ ...prev, timeSlotsEnabled: checked }))} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400">Durée des créneaux (minutes, optionnel)</Label>
              <Input
                type="number"
                min="5"
                max="240"
                value={eventFormData.slotDurationMinutes}
                onChange={(e) => setEventFormData(prev => ({ ...prev, slotDurationMinutes: e.target.value }))}
                className="text-white bg-gray-900 border-gray-700"
                placeholder="30"
              />
            </div>
            <div className="flex gap-3 justify-end pt-4"><Button type="button" variant="outline" onClick={() => setIsEventModalOpen(false)} className="border-gray-700">Annuler</Button><Button type="submit" className="text-black bg-white hover:bg-gray-200">{selectedEvent ? 'Enregistrer' : 'Créer'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isLineupModalOpen} onOpenChange={setIsLineupModalOpen}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white w-[95vw] sm:w-auto sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Line-up — Vol.{selectedEvent?.volume} {selectedEvent?.theme}</DialogTitle>
            <DialogDescription>Artistes, horaires, preview audio et liens</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveLineup} className="mt-4 space-y-4">
            <div className="flex gap-3 items-center p-4 bg-gray-900 rounded-lg border border-white/10"><Label className="text-white">Visible</Label><Switch checked={!!lineupFormData.visible} onCheckedChange={(checked) => setLineupFormData(prev => ({ ...prev, visible: checked }))} /></div>
            {lineupFormData.artists.map((artist, index) => (
              <div key={index} className="p-4 bg-gray-900 rounded-lg border border-white/10">
                <div className="flex justify-between items-start mb-3"><h4 className="font-semibold">Artiste {index + 1}</h4><Button type="button" variant="ghost" size="sm" onClick={() => removeArtist(index)} className="text-gray-400 hover:text-red-400"><Trash2 size={16} /></Button></div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label className="text-gray-400">Nom</Label><Input value={artist.name} onChange={(e) => updateArtist(index, 'name', e.target.value)} required className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Rôle</Label><Input value={artist.role || ''} onChange={(e) => updateArtist(index, 'role', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Début</Label><Input value={artist.startTime || ''} onChange={(e) => updateArtist(index, 'startTime', e.target.value)} placeholder="21:00" className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Fin</Label><Input value={artist.endTime || ''} onChange={(e) => updateArtist(index, 'endTime', e.target.value)} placeholder="22:30" className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">SoundCloud</Label><Input value={artist.soundcloudUrl || ''} onChange={(e) => updateArtist(index, 'soundcloudUrl', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Spotify</Label><Input value={artist.spotifyUrl || ''} onChange={(e) => updateArtist(index, 'spotifyUrl', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Instagram</Label><Input value={artist.instagram || ''} onChange={(e) => updateArtist(index, 'instagram', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Facebook</Label><Input value={artist.facebook || ''} onChange={(e) => updateArtist(index, 'facebook', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2"><Label className="text-gray-400">Autre lien</Label><Input value={artist.otherLink || ''} onChange={(e) => updateArtist(index, 'otherLink', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2 sm:col-span-2"><Label className="text-gray-400">Avatar URL</Label><Input value={artist.avatarUrl || ''} onChange={(e) => updateArtist(index, 'avatarUrl', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                  <div className="space-y-2 sm:col-span-2"><Label className="text-gray-400">Bio</Label><Textarea rows={2} value={artist.description || ''} onChange={(e) => updateArtist(index, 'description', e.target.value)} className="text-white bg-gray-800 border-gray-700" /></div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addArtist} className="w-full text-gray-400 border-gray-700 border-dashed"><Plus size={18} className="mr-2" />Ajouter un artiste</Button>
            <div className="flex gap-3 justify-end pt-4"><Button type="button" variant="outline" onClick={() => setIsLineupModalOpen(false)} className="border-gray-700">Annuler</Button><Button type="submit" className="text-black bg-white hover:bg-gray-200">Enregistrer</Button></div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Menu Modal */}
      <Dialog open={isMenuModalOpen} onOpenChange={setIsMenuModalOpen}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white w-[95vw] sm:w-auto sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Menu — Vol.{selectedEvent?.volume} {selectedEvent?.theme}</DialogTitle>
            <DialogDescription>Ajouter et modifier les plats pour cet événement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveMenu} className="mt-4 space-y-4">
            <Tabs defaultValue="foods" className="w-full">
              <TabsList className="mb-4 bg-gray-900 border border-white/10">
                <TabsTrigger value="foods" className="data-[state=active]:bg-white data-[state=active]:text-black">Plats</TabsTrigger>
                <TabsTrigger value="drinks" className="data-[state=active]:bg-white data-[state=active]:text-black">Boissons</TabsTrigger>
              </TabsList>
              <TabsContent value="foods" className="space-y-4">
                <Accordion type="single" collapsible className="w-full">
                  {menuFormData.dishes
                    .map((dish, index) => ({ dish, index }))
                    .filter(({ dish }) => dish.type !== 'drink')
                    .map(({ dish, index }) => (
                      <AccordionItem key={`food-${index}`} value={`food-${index}`} className="px-2 bg-gray-900 rounded-lg border border-white/10">
                        <AccordionTrigger className="px-2 py-3">
                          <div className="flex justify-between items-center pr-2 w-full">
                            <div className="flex-1 text-left">
                              <div className="font-semibold">{dish.name || 'Sans nom'}</div>
                              <div className="text-xs text-gray-400">{dish.type || 'type'}{dish.price != null ? ` • ${dish.price}€` : ''}</div>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeDish(index); }} className="text-gray-400 hover:text-red-400"><X size={16} /></Button>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-2 pb-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="space-y-2"><Label className="text-gray-400">Nom</Label><Input value={dish.name} onChange={(e) => updateDish(index, 'name', e.target.value)} required className="text-white bg-gray-800 border-gray-700" placeholder="La Tartiflette" /></div>
                            <div className="space-y-2"><Label className="text-gray-400">Type</Label>
                              <Select value={dish.type} onValueChange={(value) => updateDish(index, 'type', value)}>
                                <SelectTrigger className="text-white bg-gray-800 border-gray-700"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-gray-900 border-gray-700">
                                  <SelectItem value="starter" className="text-white">Entrée</SelectItem>
                                  <SelectItem value="main" className="text-white">Plat</SelectItem>
                                  <SelectItem value="dessert" className="text-white">Dessert</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2"><Label className="text-gray-400">Prix (€)</Label><Input type="number" min="0" value={dish.price ?? 0} onChange={(e) => updateDish(index, 'price', e.target.value)} className="text-white bg-gray-800 border-gray-700" placeholder="0" /></div>
                          </div>
                          <div className="mb-4 space-y-2">
                            <div className="flex justify-between items-center">
                              <Label className="text-gray-400">Ingrédients</Label>
                              <Button type="button" variant="ghost" size="sm" onClick={() => addIngredient(index)} className="px-2 h-6 text-gray-400 hover:text-white">
                                <Plus size={14} className="mr-1" />
                                Ajouter
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {(dish.ingredients || []).length === 0 ? (
                                <div className="py-2 text-sm italic text-gray-500">Aucun ingrédient. Cliquez sur "Ajouter" pour en ajouter.</div>
                              ) : (
                                (dish.ingredients || []).map((ingredient, ingIndex) => (
                                  <div key={ingIndex} className="flex gap-2 items-center">
                                    <Input value={ingredient} onChange={(e) => updateIngredient(index, ingIndex, e.target.value)} className="flex-1 text-white bg-gray-800 border-gray-700" placeholder="Ex: Pommes grenailles rôties" />
                                    <Button type="button" variant="ghost" size="sm" onClick={() => removeIngredient(index, ingIndex)} className="p-0 w-9 h-9 text-gray-400 hover:text-red-400">
                                      <X size={14} />
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                          <div className="mb-4 space-y-2">
                            <Label className="text-gray-400">Description</Label>
                            <Textarea value={dish.description || ''} onChange={(e) => updateDish(index, 'description', e.target.value)} className="text-white bg-gray-800 border-gray-700" placeholder="Description du plat (optionnel)" rows={2} />
                          </div>
                          <div className="flex flex-wrap gap-4 items-center">
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`veg-${index}`} checked={!!dish.vegetarian} onChange={(e) => updateDish(index, 'vegetarian', e.target.checked)} className="rounded" />
                              <Label htmlFor={`veg-${index}`} className="flex gap-1 items-center text-gray-400"><Leaf size={14} className="text-green-500" />Végétarien</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`meat-${index}`} checked={!!dish.meat} onChange={(e) => updateDish(index, 'meat', e.target.checked)} className="rounded" />
                              <Label htmlFor={`meat-${index}`} className="flex gap-1 items-center text-gray-400"><Drumstick size={14} className="text-red-400" />Viande</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`spicy-${index}`} checked={!!dish.spicy} onChange={(e) => updateDish(index, 'spicy', e.target.checked)} className="rounded" />
                              <Label htmlFor={`spicy-${index}`} className="flex gap-1 items-center text-gray-400"><Flame size={14} className="text-orange-400" />Épicé</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`fish-${index}`} checked={!!dish.fish} onChange={(e) => updateDish(index, 'fish', e.target.checked)} className="rounded" />
                              <Label htmlFor={`fish-${index}`} className="flex gap-1 items-center text-gray-400"><Fish size={14} className="text-blue-400" />Poisson</Label>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                </Accordion>
                <Button type="button" variant="outline" onClick={() => addDishOfType('main')} className="w-full text-gray-400 border-gray-700 border-dashed"><Plus size={18} className="mr-2" />Ajouter un plat</Button>
              </TabsContent>
              <TabsContent value="drinks" className="space-y-4">
                <Accordion type="single" collapsible className="w-full">
                  {menuFormData.dishes
                    .map((dish, index) => ({ dish, index }))
                    .filter(({ dish }) => dish.type === 'drink')
                    .map(({ dish, index }) => (
                      <AccordionItem key={`drink-${index}`} value={`drink-${index}`} className="px-2 bg-gray-900 rounded-lg border border-white/10">
                        <AccordionTrigger className="px-2 py-3">
                          <div className="flex justify-between items-center pr-2 w-full">
                            <div className="flex-1 text-left">
                              <div className="font-semibold">{dish.name || 'Sans nom'}</div>
                              <div className="text-xs text-gray-400">boisson{dish.price != null ? ` • ${dish.price}€` : ''}</div>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeDish(index); }} className="text-gray-400 hover:text-red-400"><X size={16} /></Button>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-2 pb-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="space-y-2"><Label className="text-gray-400">Nom</Label><Input value={dish.name} onChange={(e) => updateDish(index, 'name', e.target.value)} required className="text-white bg-gray-800 border-gray-700" placeholder="Mojito" /></div>
                            <div className="space-y-2"><Label className="text-gray-400">Type</Label>
                              <Select value="drink" onValueChange={() => {}} disabled>
                                <SelectTrigger className="text-white bg-gray-800 border-gray-700"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-gray-900 border-gray-700">
                                  <SelectItem value="drink" className="text-white">Boisson</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2"><Label className="text-gray-400">Prix (€)</Label><Input type="number" min="0" value={dish.price ?? 0} onChange={(e) => updateDish(index, 'price', e.target.value)} className="text-white bg-gray-800 border-gray-700" placeholder="0" /></div>
                          </div>
                          <div className="mb-4 space-y-2">
                            <Label className="text-gray-400">Description</Label>
                            <Textarea value={dish.description || ''} onChange={(e) => updateDish(index, 'description', e.target.value)} className="text-white bg-gray-800 border-gray-700" placeholder="Description de la boisson (optionnel)" rows={2} />
                          </div>
                          <div className="flex flex-wrap gap-4 items-center">
                            <div className="flex gap-2 items-center">
                              <input
                                type="checkbox"
                                id={`alcohol-${index}`}
                                checked={!!dish.alcoholic}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  updateDish(index, 'alcoholic', checked);
                                  if (checked) updateDish(index, 'nonAlcoholic', false);
                                }}
                                className="rounded"
                              />
                              <Label htmlFor={`alcohol-${index}`} className="flex gap-1 items-center text-gray-400">Alcool</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input
                                type="checkbox"
                                id={`nonalcohol-${index}`}
                                checked={!!dish.nonAlcoholic}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  updateDish(index, 'nonAlcoholic', checked);
                                  if (checked) updateDish(index, 'alcoholic', false);
                                }}
                                className="rounded"
                              />
                              <Label htmlFor={`nonalcohol-${index}`} className="flex gap-1 items-center text-gray-400">Sans alcool</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`veg-${index}`} checked={!!dish.vegetarian} onChange={(e) => updateDish(index, 'vegetarian', e.target.checked)} className="rounded" />
                              <Label htmlFor={`veg-${index}`} className="flex gap-1 items-center text-gray-400">Végétarien</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`meat-${index}`} checked={!!dish.meat} onChange={(e) => updateDish(index, 'meat', e.target.checked)} className="rounded" />
                              <Label htmlFor={`meat-${index}`} className="flex gap-1 items-center text-gray-400">Viande</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`spicy-${index}`} checked={!!dish.spicy} onChange={(e) => updateDish(index, 'spicy', e.target.checked)} className="rounded" />
                              <Label htmlFor={`spicy-${index}`} className="flex gap-1 items-center text-gray-400">Épicé</Label>
                            </div>
                            <div className="flex gap-2 items-center">
                              <input type="checkbox" id={`fish-${index}`} checked={!!dish.fish} onChange={(e) => updateDish(index, 'fish', e.target.checked)} className="rounded" />
                              <Label htmlFor={`fish-${index}`} className="flex gap-1 items-center text-gray-400">Poisson</Label>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                </Accordion>
                <Button type="button" variant="outline" onClick={() => addDishOfType('drink')} className="w-full text-gray-400 border-gray-700 border-dashed"><Plus size={18} className="mr-2" />Ajouter une boisson</Button>
              </TabsContent>
            </Tabs>
            <div className="flex gap-3 justify-end pt-2"><Button type="button" variant="outline" onClick={() => setIsMenuModalOpen(false)} className="border-gray-700">Annuler</Button><Button type="submit" className="text-black bg-white hover:bg-gray-200">Enregistrer</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Modal */}
      <Dialog open={isMediaModalOpen} onOpenChange={setIsMediaModalOpen}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white w-[95vw] sm:w-auto sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Media — Vol.{selectedEvent?.volume} {selectedEvent?.theme}</DialogTitle>
            <DialogDescription>Gérer les médias associés à cet événement.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="flex flex-col gap-3 p-4 bg-gray-900 rounded-lg border sm:flex-row sm:justify-between sm:items-center border-white/10">
              <div>
                <Label className="text-gray-400">Afficher les médias de l'événement</Label>
                <p className="text-sm text-gray-500">Contrôle la visibilité sur la page d'accueil</p>
              </div>
              <div className="flex gap-3 items-center">
                <Badge className={selectedEvent?.mediaActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-300'}>
                  {selectedEvent?.mediaActive ? 'Actif' : 'Inactif'}
                </Badge>
                <Button onClick={toggleEventMediaActive} disabled={mediaActiveUpdating} className={selectedEvent?.mediaActive ? 'bg-green-600 hover:bg-green-500 text-black' : 'bg-gray-700 hover:bg-gray-600 text-white'}>
                  {selectedEvent?.mediaActive ? 'Masquer les médias' : 'Afficher les médias'}
                </Button>
              </div>
            </div>
            {mediaActiveMessage && (
              <div className="px-4 py-2 text-sm text-gray-300 bg-gray-800 rounded border border-white/10">{mediaActiveMessage}</div>
            )}
            <div className="p-4 bg-gray-900 rounded-lg border border-white/10">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                  <Label className="text-gray-400">Ajouter des médias</Label>
                  <p className="text-sm text-gray-500">JPG, PNG, MP4 ou lien Instagram</p>
                </div>
                <div>
                  <input type="file" multiple accept="image/jpeg,image/png,video/mp4" onChange={(e) => handleUploadMedia(e.target.files)} className="text-sm" />
                </div>
              </div>
              {uploadingMedia && <p className="mt-2 text-sm text-gray-400">Téléversement...</p>}
              <div className="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-gray-400">Lien</Label>
                  <Input id="external-url" placeholder="https://www.instagram.com/p/..." className="text-white bg-gray-800 border-gray-700" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Type</Label>
                  <Input value="Instagram" readOnly className="text-white bg-gray-800 border-gray-700" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Légende</Label>
                  <Input id="external-caption" placeholder="Légende (optionnel)" className="text-white bg-gray-800 border-gray-700" />
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <Button
                  variant="outline"
                  className="border-gray-700"
                  onClick={async () => {
                    const url = document.getElementById('external-url')?.value?.trim();
                    const type = 'instagram';
                    const caption = document.getElementById('external-caption')?.value || '';
                    if (!url) return;
                    try {
                      await adminAPI.post(`/media`, { eventId: selectedEvent.id, type, url, caption, visible: true });
                      const res = await adminAPI.get(`/media/${selectedEvent.id}`);
                      setMediaItems(res.data.media || []);
                    } catch (err) {
                      alert('Erreur lors de l’ajout du lien');
                    }
                  }}
                >
                  Ajouter le lien
                </Button>
              </div>
              <div className="flex flex-col gap-2 justify-end mt-3 sm:flex-row">
                <Button variant="outline" size="sm" onClick={() => openSelectionModal('event', selectedEvent?.id)} className="w-full text-gray-300 border-gray-700 sm:w-auto hover:bg-gray-800">Ajouter depuis la bibliothèque</Button>
              </div>
            </div>
            {mediaItems.length === 0 ? (
              <div className="p-8 text-center text-gray-500 bg-gray-900 rounded-lg border border-white/10">Aucun média</div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {mediaItems.map((m, idx) => (
                  <div key={m.id} className="overflow-hidden bg-gray-900 rounded-lg border border-white/10">
                    <div className="flex justify-center items-center bg-black aspect-video">
                      {m.type === 'image' ? (
                        <img src={mediaSrc(m.url)} alt={m.caption || `Media ${idx+1}`} className="object-cover w-full h-full" loading="lazy" />
                      ) : m.type === 'video' ? (
                        <video src={mediaSrc(m.url)} className="object-cover w-full h-full" controls preload="metadata" />
                      ) : m.type === 'instagram' ? (
                        <iframe src={toInstagramEmbed(m.url) || m.url} className="w-full h-full" allowTransparency="true" frameBorder="0" scrolling="no" />
                      ) : null}
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                        <Badge className={
                          m.type === 'image' ? 'bg-blue-500/20 text-blue-400' :
                          m.type === 'video' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-pink-500/20 text-pink-400'
                        }>
                          {m.type === 'image' ? <ImageIcon size={12} className="mr-1" /> : <Video size={12} className="mr-1" />} {m.type}
                        </Badge>
                        <div className="flex flex-wrap gap-2 items-center">
                          <Button variant="ghost" size="sm" onClick={() => moveMedia(idx, -1)} className="text-gray-400 hover:text-white"><MoveUp size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => moveMedia(idx, 1)} className="text-gray-400 hover:text-white"><MoveDown size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteMedia(m.id)} className="text-gray-400 hover:text-red-400"><Trash2 size={14} /></Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-400">Légende</Label>
                        <Input value={m.caption || ''} onChange={(e) => updateMedia(m.id, { caption: e.target.value })} className="text-sm text-white bg-gray-800 border-gray-700" placeholder="Ajouter une légende" />
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                        <div>
                          <Label className="text-white">Visible</Label>
                          <p className="text-sm text-gray-500">Afficher sur le site</p>
                        </div>
                        <Switch checked={!!m.visible} onCheckedChange={(checked) => updateMedia(m.id, { visible: checked })} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setIsMediaModalOpen(false)} className="border-gray-700">Fermer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Media Selection Modal */}
      <Dialog open={isSelectionModalOpen} onOpenChange={setIsSelectionModalOpen}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white w-[95vw] sm:w-auto sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Bibliothèque de médias</DialogTitle>
            <DialogDescription>Rechercher, téléverser et sélectionner des médias.</DialogDescription>
          </DialogHeader>
          <Tabs value={selectionTab} onValueChange={setSelectionTab} className="w-full">
            <TabsList className="mb-4 bg-gray-900 border border-white/10">
              <TabsTrigger value="uploaded" className="data-[state=active]:bg-white data-[state=active]:text-black">Déjà téléchargé</TabsTrigger>
              <TabsTrigger value="upload" className="data-[state=active]:bg-white data-[state=active]:text-black">Téléverser</TabsTrigger>
            </TabsList>
            <TabsContent value="uploaded">
              <div className="flex flex-col gap-4 mb-4 md:flex-row">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-3 top-1/2 text-gray-500 -translate-y-1/2" />
                  <Input placeholder="Rechercher..." value={selectionQuery} onChange={(e) => setSelectionQuery(e.target.value)} className="pl-10 text-white bg-gray-900 border-gray-700" />
                </div>
                <Select value={selectionFilter} onValueChange={(v) => setSelectionFilter(v)}>
                  <SelectTrigger className="w-[160px] bg-gray-900 border-gray-700 text-white"><Filter size={16} className="mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all" className="text-white">Tous</SelectItem>
                    <SelectItem value="image" className="text-white">Images</SelectItem>
                    <SelectItem value="video" className="text-white">Vidéos</SelectItem>
                    <SelectItem value="document" className="text-white">Documents</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={loadSelectionList} variant="outline" className="text-white border-white/20 hover:bg-white/10">Actualiser</Button>
              </div>
              {selectionLoading ? (
                <div className="p-8 text-center text-gray-500 bg-gray-900 rounded-lg border border-white/10">Chargement...</div>
              ) : selectionList.length === 0 ? (
                <div className="p-8 text-center text-gray-500 bg-gray-900 rounded-lg border border-white/10">Aucun média trouvé</div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {selectionList.map((m) => (
                    <button key={m.id} onClick={() => toggleSelection(m.id)} className={`group relative rounded-lg overflow-hidden border ${selectionSelectedIds.includes(m.id) ? 'border-white' : 'border-white/10'} bg-gray-900`}>
                      <div className="flex justify-center items-center bg-black aspect-square">
                        {m.type === 'image' ? (
                          <img src={mediaSrc(m.url)} alt={m.caption || ''} className="object-cover w-full h-full" loading="lazy" />
                        ) : m.type === 'video' ? (
                          <video src={mediaSrc(m.url)} className="object-cover w-full h-full" preload="metadata" />
                        ) : (
                          <div className="flex justify-center items-center w-full h-full text-gray-300">Document</div>
                        )}
                      </div>
                      <div className="p-2 text-sm text-gray-300 truncate">{m.caption || 'Sans légende'}</div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center mt-4">
                {selectionMode === 'event' && (
                  <Select value={selectionEventId || selectedEvent?.id || ''} onValueChange={setSelectionEventId}>
                    <SelectTrigger className="w-[220px] bg-gray-900 border-gray-700 text-white"><SelectValue placeholder="Choisir l'événement" /></SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      {events.map(ev => (
                        <SelectItem key={ev.id} value={ev.id} className="text-white">Vol.{ev.volume} — {ev.theme}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  {selectionMode === 'featured' ? (
                    <Button onClick={addSelectedToFeatured} className="text-black bg-white hover:bg-gray-200">Ajouter à la Une</Button>
                  ) : (
                    <Button onClick={addSelectedToEvent} className="text-black bg-white hover:bg-gray-200">Ajouter à l'événement</Button>
                  )}
                  <Button variant="outline" onClick={() => setIsSelectionModalOpen(false)} className="border-gray-700">Fermer</Button>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="upload">
              <div className="p-4 bg-gray-900 rounded-lg border border-white/10">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="text-gray-400">Fichiers</Label>
                    <input type="file" multiple accept="image/jpeg,image/png,video/mp4,application/pdf" onChange={async (e) => {
                      const files = e.target.files;
                      if (!files) return;
                      setUploadProgress(0);
                      for (const file of files) {
                        const form = new FormData();
                        form.append('file', file);
                        form.append('visible', 'true');
                        await adminAPI.post(`/media/upload-global`, form, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          onUploadProgress: (p) => {
                            const percent = Math.round((p.loaded * 100) / (p.total || 1));
                            setUploadProgress(percent);
                          }
                        });
                      }
                      await loadSelectionList();
                    }} className="text-sm" />
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2 text-sm text-gray-400">Téléversement: {uploadProgress}%</div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Reservation Add/Edit/Delete */}
      <Dialog open={isAddModalOpen} onOpenChange={(open) => { setIsAddModalOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg text-white border-gray-800 bg-gray-950">
          <DialogHeader>
            <DialogTitle>Nouvelle réservation</DialogTitle>
            <DialogDescription>Créer une nouvelle réservation manuelle.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddReservation} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2"><Label className="text-gray-400">Nom</Label><Input value={formData.name} minLength={2} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Téléphone</Label><Input value={formData.phone} minLength={8} maxLength={20} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
              <div className="col-span-2 space-y-2">
                <Label className="text-gray-400">Événement</Label>
                <Select value={formData.eventVolume} onValueChange={(v) => setFormData(prev => ({ ...prev, eventVolume: v }))}>
                  <SelectTrigger className="text-white bg-gray-900 border-gray-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {events.map(ev => (
                      <SelectItem key={ev.id} value={ev.volume.toString()} className="text-white">Vol.{ev.volume} — {ev.theme}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="text-gray-400">Personnes</Label><Select value={formData.guests} onValueChange={(v) => setFormData(prev => ({ ...prev, guests: v }))}><SelectTrigger className="text-white bg-gray-900 border-gray-700"><SelectValue /></SelectTrigger><SelectContent className="bg-gray-900 border-gray-700">{[1,2,3,4,5,6].map(n => <SelectItem key={n} value={n.toString()} className="text-white">{n}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2 space-y-2">
                <Label className="text-gray-400">Sélection du menu</Label>
                <MenuSelector />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4"><Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-gray-700">Annuler</Button><Button type="submit" className="text-black bg-white hover:bg-gray-200">Créer</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditModalOpen} onOpenChange={(open) => { setIsEditModalOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg text-white border-gray-800 bg-gray-950">
          <DialogHeader>
            <DialogTitle>Modifier la réservation</DialogTitle>
            <DialogDescription>Mettre à jour les informations de la réservation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditReservation} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2"><Label className="text-gray-400">Nom</Label><Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
              <div className="space-y-2"><Label className="text-gray-400">Téléphone</Label><Input value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} required className="text-white bg-gray-900 border-gray-700" /></div>
            </div>
            <div className="space-y-2"><Label className="text-gray-400">Statut</Label><Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}><SelectTrigger className="text-white bg-gray-900 border-gray-700"><SelectValue /></SelectTrigger><SelectContent className="bg-gray-900 border-gray-700"><SelectItem value="confirmed" className="text-white">Confirmé</SelectItem><SelectItem value="pending" className="text-white">En attente</SelectItem><SelectItem value="cancelled" className="text-white">Annulé</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label className="text-gray-400">Sélection du menu</Label><MenuSelector /></div>
            <div className="flex gap-3 justify-end pt-4"><Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-gray-700">Annuler</Button><Button type="submit" className="text-black bg-white hover:bg-gray-200">Enregistrer</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md text-white border-gray-800 bg-gray-950">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <p className="py-4 text-gray-400">Supprimer la réservation de <span className="font-semibold text-white">{selectedReservation?.name}</span> ?</p>
          <div className="flex gap-3 justify-end"><Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} className="border-gray-700">Annuler</Button><Button onClick={handleDeleteReservation} className="bg-red-600 hover:bg-red-700">Supprimer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation Modal */}
      <Dialog open={isDeleteClientModalOpen} onOpenChange={setIsDeleteClientModalOpen}>
        <DialogContent className="max-w-md text-white border-gray-800 bg-gray-950">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400">Supprimer le client</DialogTitle>
            <DialogDescription>Supprimer définitivement ce client et ses données associées.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-gray-300">Êtes-vous sûr de vouloir supprimer ce client ?</p>
            {selectedClient && (
              <div className="p-4 space-y-2 bg-gray-900 rounded-lg">
                <div className="flex justify-between items-center"><span className="text-gray-400">Email:</span><span className="font-medium">{selectedClient.email}</span></div>
                {selectedClient.name && (<div className="flex justify-between items-center"><span className="text-gray-400">Nom:</span><span className="font-medium">{selectedClient.name}</span></div>)}
                <div className="flex justify-between items-center"><span className="text-gray-400">Source:</span><Badge className={selectedClient.source === 'reservation' ? 'bg-blue-500/20 text-blue-400' : selectedClient.source === 'both' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}>{selectedClient.source === 'reservation' ? 'Réservation' : selectedClient.source === 'both' ? 'Les deux' : 'Newsletter'}</Badge></div>
              </div>
            )}
            <div className="p-3 rounded-lg border bg-yellow-500/10 border-yellow-500/20"><p className="text-sm text-yellow-400">⚠️ Cette action supprimera toutes les données associées à ce client (réservations et abonnements newsletter).</p></div>
            <div className="flex gap-3 justify-end pt-4"><Button type="button" variant="outline" onClick={() => setIsDeleteClientModalOpen(false)} className="border-gray-700">Annuler</Button><Button onClick={handleDeleteClient} className="text-white bg-red-600 hover:bg-red-700"><Trash2 size={16} className="mr-2" />Supprimer</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

AdminModals.propTypes = {};

export default AdminModals;
